import { createFileRoute } from "@tanstack/react-router";
import { faqs } from "@/data/content";
import { site, pageUrl } from "@/data/site";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { FAQAccordion } from "@/components/site/faq-accordion";
import { ProcessTimeline, FinalCTA } from "@/components/site/final-cta";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ | Custom Orders, Pricing & Payment | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "How custom orders work, how pricing and PayPal invoicing are handled, whether you can supply your own material, and how to reach Paul directly.",
      },
      { property: "og:title", content: "Questions about ordering from Paul" },
      { property: "og:description", content: "Custom orders, pricing, payment, materials and turnaround, answered plainly." },
      { property: "og:url", content: pageUrl("/faq") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/faq") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Faq,
});

function Faq() {
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "FAQ" }]} />
      <PageHeader
        eyebrow="Questions"
        title="How ordering from a one-person workshop works."
        lede="Straight answers about pricing, payment, materials and timing. If your question is not here, Paul would rather you just asked him."
      />

      <Section tone="paper" className="pt-0">
        <div className="shell grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div className="lg:sticky lg:top-28">
            <SectionHeading level={2} eyebrow="Still unsure?" title="Ask Paul directly." />
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Every project starts with a conversation, so there is no wrong question.
            </p>
            <a
              href={`mailto:${site.email}`}
              className="mt-5 block font-[family-name:var(--font-display)] text-xl underline underline-offset-4"
            >
              {site.email}
            </a>
            <a href={`tel:${site.phoneHref}`} className="mt-2 block text-lg text-walnut underline underline-offset-4">
              {site.phoneDisplay}
            </a>
          </div>
          <FAQAccordion items={faqs} showPendingNote />
        </div>
      </Section>

      <Section labelledBy="faq-process-heading">
        <div className="shell">
          <SectionHeading id="faq-process-heading" eyebrow="The process" title="From idea to finished piece." />
          <div className="mt-10">
            <ProcessTimeline />
          </div>
        </div>
      </Section>

      <FinalCTA />
    </>
  );
}
